import { useState } from 'react'
import { User, Lock, ShieldCheck, Server, Key, Save, CheckCircle2 } from 'lucide-react'
import { Button, Card, CardContent, Input } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { useAuth } from '@/providers/auth-provider'
import { toast } from 'sonner'

export function SettingsPage() {
  const { user } = useAuth()
  const [isSaving, setIsSaving] = useState(false)

  // Change password form state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // System options state
  const [requireEmailVerification, setRequireEmailVerification] = useState(true)
  const [autoApproveSellers, setAutoApproveSellers] = useState(false)

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields.')
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match.')
      return
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long.')
      return
    }

    setIsSaving(true)
    setTimeout(() => {
      setIsSaving(false)
      toast.success('Admin password updated successfully.')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    }, 600)
  }

  const handleSavePlatformSettings = () => {
    toast.success('Platform security configuration updated.')
  }

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <SectionHeading
        eyebrow="System Configuration"
        title="Admin Settings"
        description="Manage administrator profile credentials, security policies, and system preferences."
        align="left"
      />

      {/* Admin Profile Overview */}
      <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-2 border-b border-border/60 pb-3">
            <User className="size-5 text-primary" />
            <h3 className="font-display text-base font-bold text-foreground">
              Administrator Profile
            </h3>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">First Name</label>
              <Input value={user?.firstName ?? 'Platform'} readOnly className="bg-background/60" />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Last Name</label>
              <Input value={user?.lastName ?? 'Admin'} readOnly className="bg-background/60" />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs font-semibold text-muted-foreground">Email Address</label>
              <Input value={user?.email ?? 'admin@dealercommerce.com'} readOnly className="bg-background/60 font-medium" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Form */}
      <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-2 border-b border-border/60 pb-3">
            <Lock className="size-5 text-primary" />
            <h3 className="font-display text-base font-bold text-foreground">
              Security & Credentials
            </h3>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground">Current Password</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••••••"
                className="bg-background"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">New Password</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="bg-background"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">Confirm New Password</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  className="bg-background"
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={isSaving} className="gap-2">
                <Save className="size-4" /> {isSaving ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Platform Verification Controls */}
      <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-2 border-b border-border/60 pb-3">
            <ShieldCheck className="size-5 text-primary" />
            <h3 className="font-display text-base font-bold text-foreground">
              Platform Verification Controls
            </h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-xl border border-border/60 p-4 bg-background/50">
              <div>
                <p className="text-sm font-bold text-foreground">Require Email Verification</p>
                <p className="text-xs text-muted-foreground">
                  Block unverified emails from accessing merchant portals.
                </p>
              </div>
              <input
                type="checkbox"
                checked={requireEmailVerification}
                onChange={(e) => setRequireEmailVerification(e.target.checked)}
                className="size-5 rounded border-border text-primary focus:ring-primary"
              />
            </div>

            <div className="flex items-center justify-between gap-4 rounded-xl border border-border/60 p-4 bg-background/50">
              <div>
                <p className="text-sm font-bold text-foreground">Auto-Approve Individual Sellers</p>
                <p className="text-xs text-muted-foreground">
                  Bypass admin approval queue for standard individual listings.
                </p>
              </div>
              <input
                type="checkbox"
                checked={autoApproveSellers}
                onChange={(e) => setAutoApproveSellers(e.target.checked)}
                className="size-5 rounded border-border text-primary focus:ring-primary"
              />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={handleSavePlatformSettings} className="gap-2">
              <CheckCircle2 className="size-4 text-emerald-500" /> Save Platform Policies
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
